import { cn } from '@rocco/utils/cn';
import { BASE_STYLE } from './style';
import { useEffect, useRef, useState } from 'react';
import { Dialog, pxToRem } from '@archipro-design/aria';
import { CloseLine } from '@archipro-design/icons';

/**
 * Displays detailed information about a professional including their summary and contact options
 */

interface ProfileDetailsProps {
    name: string;
    summary?: string;
    licenseNumber?: string;
    professionalCountry?: string;
}

export const ProfileDetails = ({
    name,
    summary,
    licenseNumber,
    professionalCountry,
}: ProfileDetailsProps) => {
    // Split name into first and last name
    const nameParts = name.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Get the appropriate license number label based on the professional's country
    const getLicenseNumberLabel = () => {
        switch (professionalCountry) {
            case 'NZ':
                return 'NZRAB';
            case 'AU':
                return 'AACA';
            default:
                return 'LICENSE NUMBER';
        }
    };

    const licenseNumberLabel = getLicenseNumberLabel();

    // We'll render a simple 5-line visual clamp for the summary and provide a modal to view full text.

    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalContent, setModalContent] = useState('');
    const [isOverflowing, setIsOverflowing] = useState(false);
    const summaryRef = useRef<HTMLDivElement>(null);

    const openSummaryModal = (title: string, content: string) => {
        setModalTitle(title);
        setModalContent(content);
        setModalOpen(true);
    };

    const renderTruncatedSummary = (text?: string, title?: string) => {
        if (!text) return null;

        return (
            <div
                ref={summaryRef}
                className="line-clamp-5 cursor-pointer"
                style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
                onClick={() =>
                    isOverflowing && openSummaryModal(title || 'Details', text)
                }
                title={isOverflowing ? 'Click to read more' : undefined}
            >
                {text}
            </div>
        );
    };

    useEffect(() => {
        const element = summaryRef.current;
        if (!element) return;

        // Check if text is overflowing by comparing scrollHeight to clientHeight
        const checkOverflow = () => {
            setIsOverflowing(element.scrollHeight > element.clientHeight);
        };

        checkOverflow();

        // Check on resize
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [summary]);

    return (
        <div
            className={cn(BASE_STYLE.TRANSITION_STANDARD, 'w-full opacity-100')}
        >
            <div className="my-0 mb-1 w-225">
                {licenseNumber && (
                    <p
                        className={cn(
                            BASE_STYLE.TEXT_BASE,
                            'mb-5.5 font-display text-base font-normal text-[#8E8E8E]'
                        )}
                    >
                        Registered Architect {licenseNumberLabel} Reg. No.{' '}
                        {licenseNumber}
                    </p>
                )}
                <h2
                    className={cn(
                        BASE_STYLE.TEXT_HEADING,
                        BASE_STYLE.TRANSITION_FAST,
                        'mb-0 font-display text-17.5 font-semibold -tracking-[0.088rem] text-[#161616]'
                    )}
                    style={{
                        ...BASE_STYLE.INLINE_STYLES.FADE_IN_FAST,
                    }}
                >
                    Meet {firstName}
                </h2>
                <h2
                    className={cn(
                        BASE_STYLE.TEXT_HEADING,
                        BASE_STYLE.TRANSITION_STANDARD,
                        'mt-0 font-display text-17.5 font-semibold -tracking-[0.088rem] text-[#161616]'
                    )}
                    style={{
                        ...BASE_STYLE.INLINE_STYLES.FADE_IN_STANDARD,
                    }}
                >
                    {lastName}
                </h2>
            </div>

            <div>
                <p
                    className={`${BASE_STYLE.TEXT_BASE} ${BASE_STYLE.TRANSITION_SLOW} mb-5 mt-5 h-fit w-270 break-words font-display text-5.5 font-[450] leading-[130%] -tracking-[1%]`}
                    style={{
                        ...BASE_STYLE.INLINE_STYLES.FADE_IN_SLOW,
                    }}
                >
                    {renderTruncatedSummary(summary)}
                </p>
            </div>

            <Dialog
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                header={{
                    content: (
                        <div className="flex w-full items-center justify-between">
                            <div>{modalTitle}</div>
                        </div>
                    ),
                }}
                headerAction={
                    <div
                        className="cursor-pointer"
                        onClick={() => setModalOpen(false)}
                    >
                        <CloseLine />
                    </div>
                }
                content={{
                    children: (
                        <div className="whitespace-pre-wrap font-display text-5.5 font-[450] tracking-[-0.018rem] text-black">
                            {modalContent}
                        </div>
                    ),
                }}
                variables={{
                    rootWidth: pxToRem(600),
                    overlayBackground: 'rgba(0,0,0,0.2)',
                }}
            />
        </div>
    );
};
